import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { hideModal } from "../../data/modal";
import { User } from "../../types/User";
import { Tenant } from "../../types/Tenant";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";

interface GenesisUserFormProps {
  mode: 'create' | 'edit';
  type: string;
  item?: User;
  tenants?: Tenant[];
  onSave: (data: Partial<User>) => void;
}

const getTenantId = (tenant: any): string => {
  if (!tenant) return '';
  if (typeof tenant === 'string') return tenant;
  return (tenant as any)["@id"] || (tenant as any).id || '';
};

const GenesisUserForm = ({ mode, item, tenants = [], onSave }: GenesisUserFormProps) => {
  const dispatch = useDispatch();
  const isCreate = mode === 'create';

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = useForm<Partial<User>>({
    defaultValues: isCreate
      ? {
          username: '',
          firstName: '',
          lastName: '',
          email: '',
          plainPassword: '',
          tenant: '',
        }
      : {
          username: item?.username || '',
          firstName: item?.firstName || '',
          lastName: item?.lastName || '',
          email: item?.email || '',
          plainPassword: '',
          tenant: getTenantId(item?.tenant),
        },
  });

  useEffect(() => {
    if (!isCreate && item) {
      reset({
        username: item.username || '',
        firstName: item.firstName || '',
        lastName: item.lastName || '',
        email: item.email || '',
        plainPassword: '',
        tenant: getTenantId(item.tenant),
      });
    }
  }, [item, isCreate, reset]);

  const handleClose = () => {
    dispatch(hideModal());
  };

  const onSubmit = (data: Partial<User>) => {
    // Prepare tenant reference
    const tenantData = data.tenant ? { "@id": data.tenant } : undefined;
    
    onSave({
      ...data,
      tenant: tenantData,
      // Don't send empty password for updates
      plainPassword: data.plainPassword || undefined,
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle>
        {isCreate ? 'Create User' : 'Edit User'}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="username"
              control={control}
              rules={{ required: 'Username is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Username *"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Controller
              name="email"
              control={control}
              rules={{ 
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email format',
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Email *"
                  fullWidth
                  type="email"
                  error={!!error}
                  helperText={error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Controller
              name="firstName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Controller
              name="plainPassword"
              control={control}
              rules={isCreate ? { required: 'Password is required' } : {}}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={isCreate ? 'Password *' : 'Password (leave empty to keep current)'}
                  fullWidth
                  type="password"
                  error={!!error}
                  helperText={error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Controller
              name="tenant"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error}>
                  <InputLabel>Tenant</InputLabel>
                  <Select
                    {...field}
                    label="Tenant"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    disabled={isSubmitting}
                  >
                    <MenuItem value="">No Tenant</MenuItem>
                    {tenants.map((tenant) => (
                      <MenuItem 
                        key={getTenantId(tenant)}
                        value={getTenantId(tenant)}
                      >
                        {tenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} startIcon={<CloseIcon />}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting}
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default GenesisUserForm;
